import React from 'react';
import { Star, Clock, Users, FileText, Calendar, ChevronRight } from 'lucide-react';

const DoctorCard = ({ doctor, onSelect, isSelected, onViewSchedule }) => {
  const fullName = `${doctor.name} ${doctor.last_name}`;
  const experienceHours = Math.round((doctor.consultation_minutes || 0) / 60);
  
  // Función auxiliar para formatear fecha
  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', { 
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Próximamente';
    }
  };

  const handleCardClick = () => {
    onSelect(doctor);
    setTimeout(() => onViewSchedule(doctor), 100);
  };

  return (
    <div 
      onClick={handleCardClick}
      className={`press-scale-card bg-surface-card p-3 md:p-5 rounded-card transition-all cursor-pointer group ${
        isSelected
          ? 'ring-2 ring-brand bg-brand-50'
          : 'hover:bg-brand-tint/30'
      }`}
    >
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Avatar y nombre al lado en móvil */}
        <div className="flex items-start gap-3 md:gap-0 md:flex-col md:items-center">
          <div className="flex-shrink-0">
            <img 
              src={doctor.avatar} 
              alt={fullName}
              className="w-16 h-16 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full object-cover border-3 border-white shadow-soft"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=786ED9&color=fff&size=112`;
              }}
            />
          </div>
          
          {/* Nombre al lado de la foto solo en móvil */}
          <div className="md:hidden flex-1">
            <h3 className="text-base font-semibold text-text-primary line-clamp-1">{fullName}</h3>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center gap-1">
                <Star className="text-amber-pill fill-amber-pill" size={14} />
                <span className="font-bold text-text-primary text-sm">{doctor.rating_professional}</span>
                <span className="text-text-secondary text-xs">({doctor.attended_users || 0})</span>
              </div>
              {doctor.state === 'available' && (
                <span className="ml-2 px-2 py-0.5 bg-mint-pill text-mint-deep rounded-full text-xs font-medium">
                  Disp.
                </span>
              )}
            </div>
          </div>
          
          {/* Badge de disponible en desktop */}
          {doctor.state === 'available' && (
            <div className="hidden md:block mt-2 text-center">
              <span className="inline-block px-2.5 py-1 bg-mint-pill text-mint-deep rounded-full ds-caption font-medium">
                Disponible
              </span>
            </div>
          )}
        </div>

        {/* Información principal */}
        <div className="flex-1 md:mt-0">
          {/* Nombre y rating solo en desktop */}
          <div className="hidden md:block mb-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-xl font-bold text-text-primary mb-1 tracking-tight">{fullName}</h3>
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-brand-tint text-brand-deep rounded-full text-sm font-medium">
                    {doctor.specialty?.name || 'Psicología'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Star className="text-amber-pill fill-amber-pill" size={16} />
                    <span className="font-bold text-text-primary">{doctor.rating_professional}</span>
                    <span className="text-text-secondary text-sm">({doctor.attended_users || 0})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Tags de padecimientos - VISIBLES EN MÓVIL Y DESKTOP */}
          {doctor.tag_ids && doctor.tag_ids.length > 0 && (
            <div className="mb-3 md:mb-4">
              <h4 className="hidden md:block text-sm font-medium text-text-secondary mb-2">
                Especialista en:
              </h4>
              <div className="flex flex-wrap gap-1 md:gap-2">
                {doctor.tag_ids.slice(0, 2).map((tag, index) => (
                  <span 
                    key={index}
                    className="px-2 py-0.5 md:px-3 md:py-1 bg-brand-tint text-brand-deep rounded-full text-xs md:text-sm font-medium"
                  >
                    {tag.name}
                  </span>
                ))}
                {doctor.tag_ids.length > 2 && (
                  <span className="px-2 py-0.5 md:px-3 md:py-1 bg-brand-50 text-text-mute rounded-full text-xs md:text-sm">
                    +{doctor.tag_ids.length - 2} más
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Stats como bullets horizontales - SOLO EN DESKTOP */}
          <div className="hidden md:block mb-4">
            <ul className="flex flex-wrap gap-6">
              <li className="flex items-center gap-2">
                <div className="bg-brand-tint p-1.5 rounded-input">
                  <Clock className="text-brand" size={16} />
                </div>
                <div>
                  <p className="ds-caption text-text-secondary">Experiencia</p>
                  <p className="font-semibold text-text-primary text-sm">{experienceHours} horas</p>
                </div>
              </li>
              
              <li className="flex items-center gap-2">
                <div className="bg-brand-tint p-1.5 rounded-input">
                  <Users className="text-brand" size={16} />
                </div>
                <div>
                  <p className="ds-caption text-text-secondary">Pacientes</p>
                  <p className="font-semibold text-text-primary text-sm">{doctor.attended_users || 0}</p>
                </div>
              </li>
              
              <li className="flex items-center gap-2">
                <div className="bg-brand-tint p-1.5 rounded-input">
                  <FileText className="text-brand" size={16} />
                </div>
                <div>
                  <p className="ds-caption text-text-secondary">Recetas</p>
                  <p className="font-semibold text-text-primary text-sm">{doctor.prescriptions_count || 0}</p>
                </div>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DoctorCard;